'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/v3/components/coreUI/dropdown';
import Icon from '@src/v3/components/coreUI/icon';
import BottomSheet from '@src/v3/components/coreUI/bottomSheet';
import { cn } from '@src/lib/utils';
import {
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js';

// Emoji flag (works on Mac/iOS/Android; often broken on Windows)
function iso2ToFlagEmoji(iso2: string): string {
  const code = iso2.toUpperCase();
  if (code.length !== 2) return '';
  const A = 0x1f1e6;
  const chars = [...code].map((c) => A + (c.charCodeAt(0) - 65));
  return String.fromCodePoint(chars[0], chars[1]);
}

// Flag image (used only on Windows where emoji flags don't render)
const FLAG_CDN = 'https://flagcdn.com';
function getFlagImageSrc(iso2: string): string {
  const code = iso2.toLowerCase();
  if (code.length !== 2) return '';
  return `${FLAG_CDN}/w40/${code}.png`;
}

function CountryFlag({
  iso2,
  className,
}: {
  iso2: string;
  className?: string;
}) {
  const [useImage, setUseImage] = useState<boolean | null>(null);
  const [imageFailed, setImageFailed] = useState<boolean>(false);

  useEffect(() => {
    if (
      typeof navigator !== 'undefined' &&
      /Windows/.test(navigator.userAgent)
    ) {
      setUseImage(true);
    } else {
      setUseImage(false);
    }
  }, []);

  // Mac / Linux / iOS / Android: use emoji (better look)
  if (useImage === false || useImage === null) {
    return (
      <span
        className={cn('text-lg leading-none shrink-0', className)}
        aria-hidden
      >
        {iso2ToFlagEmoji(iso2)}
      </span>
    );
  }

  // Windows: use image (emoji flags don't render in Chrome/Edge)
  const src = getFlagImageSrc(iso2);
  if (!src) return <span className={className} aria-hidden />;

  if (imageFailed) {
    return (
      <span
        className={cn(
          'shrink-0 flex items-center justify-center text-xs font-medium text-gray-600 bg-gray-100 rounded-sm uppercase',
          className
        )}
        style={{ width: 24, height: 18 }}
        aria-hidden
      >
        {iso2}
      </span>
    );
  }

  return (
    <Image
      src={src}
      alt=""
      width={24}
      height={16}
      className={cn('shrink-0 object-cover rounded-sm mr-1', className)}
      onError={() => setImageFailed(true)}
    />
  );
}

export type CountryCodeSelectProps = {
  value?: string;
  // eslint-disable-next-line no-unused-vars
  onChange: (dialCode: string) => void;
  disabled?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
};

export type CountryCodeOption = {
  iso2: CountryCode;
  name: string;
  dialCode: string; // e.g. "+1"
};

export default function CountryCodeSelect({
  value,
  onChange,
  disabled = false,
  triggerClassName,
  contentClassName,
}: CountryCodeSelectProps) {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [contentWidth, setContentWidth] = useState<number | undefined>(
    undefined
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchHeaderRef = useRef<HTMLDivElement>(null);

  // Mobile detection (same as ConfirmationModal)
  useEffect(() => {
    function handleResize() {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Focus search when picker opens (desktop dropdown or mobile sheet)
  useEffect(() => {
    if (open) {
      // Delay so content is mounted; slightly longer on mobile for sheet animation
      const delay = isMobile ? 10 : 0;
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, delay);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, isMobile]);

  const measureTriggerContainer = useCallback(() => {
    const trigger = triggerRef.current;
    // Trigger's parent is the country-code div; its parent is the full input wrapper (country code + input).
    const wrapper = trigger?.parentElement?.parentElement;
    if (wrapper) {
      const w = wrapper.getBoundingClientRect().width;
      setContentWidth(w);
    }
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        measureTriggerContainer();
      } else {
        setQuery('');
      }
      setOpen(next);
    },
    [measureTriggerContainer]
  );

  const handleSelectCountry = useCallback(
    (dialCode: string) => {
      onChange(dialCode);
      setOpen(false);
      setQuery('');
    },
    [onChange]
  );

  const displayNames = useMemo(() => {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  }, []);

  const countries = useMemo((): CountryCodeOption[] => {
    const list = getCountries()
      .map((iso2) => {
        const name = displayNames.of(iso2) ?? iso2;
        const dialCode = `+${getCountryCallingCode(iso2)}`;
        return { iso2, name, dialCode };
      })
      .filter((c) => !!c.name && !!c.dialCode)
      .sort((a, b) => {
        if (a.iso2 === 'US') return -1;
        if (b.iso2 === 'US') return 1;
        return a.name.localeCompare(b.name);
      });

    return list;
  }, [displayNames]);
  const selected: CountryCodeOption = (typeof value === 'string' && value
    ? countries.find((c) => c.dialCode === value)
    : undefined) ??
    countries.find((c) => c.iso2 === 'US') ??
    countries[0] ?? { iso2: 'US', name: 'United States', dialCode: '+1' };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) => {
      return (
        c.name.toLowerCase().includes(q) ||
        c.iso2.toLowerCase().includes(q) ||
        c.dialCode.replace('+', '').includes(q.replace('+', ''))
      );
    });
  }, [query, countries]);

  const triggerButton = (
    <button
      ref={triggerRef}
      type="button"
      disabled={disabled}
      onClick={isMobile ? () => setOpen(true) : undefined}
      className={cn(
        'w-full h-full flex items-center justify-center text-base sm:text-lg leading-none text-gray-1000',
        'focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        triggerClassName
      )}
    >
      <span>{selected.dialCode}</span>
    </button>
  );

  // Shared picker body: search + list (list items differ by context)
  const pickerBody = (useSheetButtons: boolean) => (
    <div className="flex flex-col flex-1 min-h-0 pointer-events-auto">
      <div
        ref={useSheetButtons ? searchHeaderRef : undefined}
        className="sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-background-200 px-3 py-4"
        style={
          useSheetButtons ? { touchAction: 'none', cursor: 'grab' } : undefined
        }
      >
        <div className="flex items-center gap-2.5">
          <Icon
            iconName="searchIcon"
            iconProps={{ className: 'w-6 h-6 text-gray-700 shrink-0' }}
          />
          <input
            ref={searchInputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Country"
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 h-full bg-transparent text-base font-normal text-gray-1000 placeholder:text-gray-600 outline-none border-0 !p-0 focus:ring-0 focus-visible:ring-0 focus:outline-none focus-visible:outline-none"
          />
        </div>
      </div>

      <div
        style={{ WebkitOverflowScrolling: 'touch' }}
        className={cn(
          'overflow-y-auto flex flex-col gap-y-2 px-1 flex-1 min-h-0',
          useSheetButtons
            ? 'touch-pan-y overscroll-contain'
            : 'min-h-[280px] max-h-[280px]'
        )}
      >
        {filtered?.map((c) =>
          useSheetButtons ? (
            <button
              key={`${c.iso2}-${c.dialCode}`}
              type="button"
              onClick={() => handleSelectCountry(c.dialCode)}
              className={cn(
                'w-full flex items-center gap-1 px-4 py-3 rounded-none cursor-pointer border-gray-100 first:border-b first:mt-0.5 last:mb-0.5',
                'hover:bg-gray-100 active:bg-gray-100 text-left text-gray-1000'
              )}
            >
              <CountryFlag iso2={c.iso2} className="w-6 h-[16px]" />
              <span className="flex-1 min-w-0 text-base leading-[24px] font-normal text-gray-1000 truncate">
                {c.name}
              </span>
              <span className="text-sm text-gray-1000 shrink-0">
                {c.dialCode}
              </span>
            </button>
          ) : (
            <DropdownMenuItem
              key={`${c.iso2}-${c.dialCode}`}
              className={cn(
                'w-full flex items-center gap-1 px-4 py-3 rounded-none cursor-pointer border-gray-100 first:border-b first:mt-0.5 last:mb-0.5',
                'hover:rounded-[36px] aria-selected:by-gray-100 focus:bg-bg-gray-100 hover:bg-gray-100 data-[highlighted]:bg-gray-100 hover:focus-visible:outline-none',
                'text-gray-1000'
              )}
              onSelect={() => handleSelectCountry(c.dialCode)}
            >
              <CountryFlag iso2={c.iso2} className="w-6 h-[16px]" />
              <span className="flex-1 min-w-0 text-base leading-[24px] font-normal text-gray-1000 truncate">
                {c.name}
              </span>
              <span className="text-sm text-gray-1000 shrink-0">
                {c.dialCode}
              </span>
            </DropdownMenuItem>
          )
        )}

        {filtered.length === 0 && (
          <div className="px-4 py-6 text-sm text-gray-600">
            No country found
          </div>
        )}
      </div>
    </div>
  );

  // Mobile: PickerBottomSheet (non-draggable, full height, close on overlay/cross)
  if (isMobile) {
    return (
      <>
        {triggerButton}
        <BottomSheet
          open={open}
          onClose={() => handleOpenChange(false)}
          className={contentClassName}
          dragHandleRef={searchHeaderRef}
        >
          <div className="flex-1 flex flex-col min-h-0">{pickerBody(true)}</div>
        </BottomSheet>
      </>
    );
  }

  // Desktop: dropdown modal
  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={8}
        avoidCollisions={true}
        style={{
          width: contentWidth ?? undefined,
          minWidth: contentWidth != null ? contentWidth : 280,
        }}
        className={cn(
          'z-[80] p-0 bg-white border border-gray-300 !rounded-lg shadow-lg',
          contentClassName
        )}
      >
        {pickerBody(false)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
